
import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";
import { GetRequest, PostRequest } from "../../../../Utils/hooks/ApiHandler";
import { baseURL } from "../../../../Utils/constants/constant";
import { Form, Offcanvas } from "react-bootstrap";
import { customStyles } from "../../../../Utils/helpers/commonhelper";
import { toast } from "sonner";

const schema = z.object({
  pipeline: z
    .object({ value: z.string(), label: z.string() })
    .nullable()
    .refine((v) => v !== null, { message: "Pipeline is required" }),
  roundRobin: z
    .object({ value: z.string(), label: z.string() })
    .nullable()
    .refine((v) => v !== null, { message: "Round Robin is required" }),
  stage: z
    .object({ value: z.string(), label: z.string() })
    .nullable()
    .refine((v) => v !== null, { message: "Stage is required" }),
  leadSource: z
    .object({ value: z.string(), label: z.string() })
    .nullable()
    .refine((v) => v !== null, { message: "Lead source is required" }),
  priority: z
    .object({ value: z.string(), label: z.string() })
    .nullable()
    .refine((v) => v !== null, { message: "Priority is required" }),
  followupDate: z
    .object({ value: z.string(), label: z.string() })
    .nullable()
    .refine((v) => v !== null, { message: "Follow-up date is required" }),
  // marked_label is a free-form object, no zod validation needed
  marked_label: z.record(z.string()).optional(),
});

const PRIORITY_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
const list = [
  "First Name",
  "Last Name",
  "Email",
  "Phone",
  // "Product",
  // "Quantity",
  // "Budget",
  "Requirement",
];

const REQUIRED_LABEL_KEYS = [""];

// Custom-field types that must NOT appear in the form mapping
// (single-option, multiple-option / multi-option, dropdown, select ...)
const EXCLUDED_CUSTOM_FIELD_TYPE = /option|dropdown|select/i;

// "First Name" -> "first_name", " role" -> "role", "name 3" -> "name_3"
const toRawKey = (name) => String(name).trim().toLowerCase().replace(/\s+/g, "_");

const getFollowupOptions = () => [
  { value: "yesterday", label: "Yesterday" },
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
];

const parseLabels = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const parseMarkedLabel = (raw) => {
  if (!raw) return {};
  if (!Array.isArray(raw) && typeof raw === "object") return raw;
  if (Array.isArray(raw)) {
    return raw.reduce((acc, item) => {
      if (item?.label) acc[item.label] = item.marked_label ?? null;
      return acc;
    }, {});
  }
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.reduce((acc, item) => {
        if (item?.label) acc[item.label] = item.marked_label ?? null;
        return acc;
      }, {});
    }
    return parsed || {};
  } catch {
    return {};
  }
};

const FacebookModal = ({
  show,
  onClose,
  page,
  form,
  editedData,
  setEditedData,
  fetchPages
}) => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });

  const customerId = customer?.customer_details?.customer_id;
  const loginUserId = customer?.customer_details?.id;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const extras = { customer, setCustomer, rf, navigate };
  const headers = { Authorization: token, Authentication: rf };
  
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [roundRobinData, setRoundRobinData] = useState([]);

  // label mapping state: { [labelKey]: selectedOption | null }
  const [labelMapping, setLabelMapping] = useState({});
  // label mapping validation errors: { [labelKey]: errorMessage }
  const [labelMappingErrors, setLabelMappingErrors] = useState({});


  // flattened custom fields (grouped + ungrouped) from fetchCustomFields
  const [dynamicFields, setDynamicFields] = useState([]);

  // Custom fields that can be mapped (dropdown / option types are skipped)
  const customMappingFields = useMemo(() => {
    const seen = new Set(list.map(toRawKey)); // avoid clashing with static keys
    return dynamicFields.reduce((acc, field) => {
      const name = (field?.label_name || "").trim();
      if (!name) return acc;
      if (EXCLUDED_CUSTOM_FIELD_TYPE.test(field?.label_type || "")) return acc;
      const rawKey = toRawKey(name);
      if (seen.has(rawKey)) return acc;
      seen.add(rawKey);
      acc.push({
        key: `custom_${field.id}`,
        id: field.id, // actual custom field id (used in custom_field payload)
        label: name,
        rawKey,
        isCustom: true,
      });
      return acc;
    }, []);
  }, [dynamicFields]);

  // Static fields first, custom fields come below "Requirement"
  const mappingFields = useMemo(
    () => [
      ...list.map((name) => ({
        key: name,
        label: name,
        rawKey: toRawKey(name),
        isCustom: false,
      })),
      ...customMappingFields,
    ],
    [customMappingFields],
  );
  const mappingKeysSignature = mappingFields.map((f) => f.key).join("|");
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      pipeline: null,
      stage: null,
      leadSource: null,
      priority: null,
      followupDate: null,
      roundRobin: null,
      marked_label: {},
    },
  });

  const selectedPipeline = watch("pipeline");

  // allLabelOptions stored in state so it updates reliably when editedData arrives
  const [allLabelOptions, setAllLabelOptions] = useState([]);
  const getFilteredOptions = (currentListKey) => {
    const usedValues = Object.entries(labelMapping)
      .filter(([key]) => key !== currentListKey)
      .map(([, opt]) => opt?.value)
      .filter(Boolean);
    return allLabelOptions.filter((opt) => !usedValues.includes(opt.value));
  };
  useEffect(() => {
    if (!show) return;
    reset({
      pipeline: null,
      stage: null,
      leadSource: null,
      priority: null,
      followupDate: null,
      roundRobin: null,
      marked_label: {},
    });
    setStages([]);
    setLabelMapping({});
    setLabelMappingErrors({});

    const fetchInitial = async () => {
      try {
        const userType = customer?.customer_details?.user_type;

        const [pipelineRes, leadSourceRes, roundRobin] = await Promise.all([
          GetRequest(
            `${baseURL}lead/fetchPipelines/${customerId}/leads/${loginUserId}/${userType}`,
            { headers },
            extras,
          ),
          GetRequest(
            `${baseURL}masters/getAllLead_source/${customerId}`,
            { headers },
            extras,
          ),
          GetRequest(
            `${baseURL}workflow/fetchDistributionWorkflow/${customerId}/${customer?.customer_details?.id}`,
            { headers },
            extras,
          ),
        ]);

        if (pipelineRes?.status === "success") {
          const opts = (pipelineRes.data || []).map((p) => ({
            value: p.pipeline_id ?? p.id ?? p._id,
            label: p.pipeline_name ?? p.name,
          }));
          setPipelines(opts);
        }

        if (leadSourceRes?.status === "success") {
          const opts = (leadSourceRes.data || []).map((s) => ({
            value: s.id,
            label: s.source_title,
          }));
          setLeadSources(opts);
        }

        if (roundRobin?.status === "success") {
          const opts = (roundRobin.data || []).map((r) => ({
            value: r.workflow_id,
            label: r.workflow_name,
          }));
          setRoundRobinData(opts);
        }
      } catch (err) {
        console.error("Initial fetch error:", err);
      }
    };

    fetchInitial();
  }, [show]);

  useEffect(() => {
    if (!selectedPipeline?.value) {
      setStages([]);
      setValue("stage", null);
      return;
    }

    const fetchStages = async () => {
      setStagesLoading(true);
      if (!editedData) {
        setValue("stage", null);
      }
      try {
        const res = await GetRequest(
          `${baseURL}lead/fetchPipelineStage/${selectedPipeline.value}/${customerId}`,
          { headers },
          extras,
        );
        if (res?.status === "success") {
          const opts = (res.data || []).map((s) => ({
            value: s.stage_id ?? s.id ?? s._id,
            label: s.stage_name ?? s.name,
          }));
          setStages(opts);

          if (editedData?.pipeline_stage_id) {
            const matched = opts.find(
              (o) => String(o.value) === String(editedData.pipeline_stage_id),
            );
            setValue(
              "stage",
              matched ?? {
                value: editedData.pipeline_stage_id,
                label: editedData.stage_name,
              },
            );
          }
        }
      } catch (err) {
        console.error("Stage fetch error:", err);
      } finally {
        setStagesLoading(false);
      }
    };

    fetchStages();
  }, [selectedPipeline?.value]);

  useEffect(() => {
    if (!editedData) return;

    reset({
      pipeline: editedData.pipeline_id
        ? {
            value: String(editedData.pipeline_id),
            label: editedData.pipeline_name,
          }
        : null,
      stage: null, // restored after stages load
      leadSource: editedData.lead_source_id
        ? {
            value: String(editedData.lead_source_id),
            label: editedData.source_title,
          }
        : null,
      roundRobin: editedData.round_robin_id
        ? {
            value: String(editedData.round_robin_id),
            label: editedData.workflow_name,
          }
        : null,
      priority: editedData.priority
        ? {
            value: editedData.priority,
            label:
              editedData.priority.charAt(0).toUpperCase() +
              editedData.priority.slice(1),
          }
        : null,
      followupDate: editedData.followup_date
        ? {
            value: editedData.followup_date,
            label:
              editedData.followup_date.charAt(0).toUpperCase() +
              editedData.followup_date.slice(1),
          }
        : null,
      marked_label: {},
    });

    const parsedLabels = parseLabels(editedData.labels);
    const opts = parsedLabels.map((l) => ({
      value: l,
      label: l.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    }));
    setAllLabelOptions(opts);
  }, [editedData]);

  // Restore saved mapping. Runs when editedData changes AND when custom fields
  // finish loading, so saved custom-field mappings are restored as well.
  // - static fields  -> editedData.marked_label  (matched by rawKey, e.g. "first_name")
  // - custom fields  -> editedData.custom_label  (matched by custom field id)
  useEffect(() => {
    if (!editedData) return;

    const parsedLabels = parseLabels(editedData.labels);
    const savedMap = parseMarkedLabel(editedData.marked_label);
    const savedCustomMap = parseMarkedLabel(editedData.custom_label);

    const hasSaved =
      Object.keys(savedMap).length > 0 ||
      Object.keys(savedCustomMap).length > 0;

    if (hasSaved && parsedLabels.length > 0) {
      const toOption = (savedVal) =>
        savedVal
          ? {
              value: savedVal,
              label: savedVal
                .replace(/_/g, " ")
                .replace(/\b\w/g, (c) => c.toUpperCase()),
            }
          : null;

      const restoredMapping = {};
      mappingFields.forEach((field) => {
        if (field.isCustom) {
          const customId = String(field.id);
          if (customId in savedCustomMap) {
            restoredMapping[field.key] = toOption(savedCustomMap[customId]);
          }
        } else if (field.rawKey in savedMap) {
          restoredMapping[field.key] = toOption(savedMap[field.rawKey]);
        }
      });
      setLabelMapping(restoredMapping);
    } else {
      setLabelMapping({});
    }
  }, [editedData, mappingKeysSignature]);

  const onSubmit = async (data) => {
    if (mappingFields.length > 0) {
      const newLabelErrors = {};
      mappingFields.forEach((field) => {
        if (REQUIRED_LABEL_KEYS.includes(field.key) && !labelMapping[field.key]) {
          newLabelErrors[field.key] = `${field.label} is required`;
        }
      });
      if (Object.keys(newLabelErrors).length > 0) {
        setLabelMappingErrors(newLabelErrors);
        return;
      }
    }
    setLabelMappingErrors({});
    setSubmitting(true);
    try {
      // Static fields -> marked_label (label = rawKey, e.g. "first_name")
      const markedLabelArray = mappingFields
        .filter((field) => !field.isCustom)
        .map((field) => ({
          label: field.rawKey,
          marked_label: labelMapping[field.key]?.value ?? null,
        }));

      // Custom fields -> custom_field (label = custom field's id)
      const customFieldArray = mappingFields
        .filter((field) => field.isCustom)
        .map((field) => ({
          label: field.id,
          marked_label: labelMapping[field.key]?.value ?? null,
        }));

      const payload = {
        id: form?.id,
        form_id: form?.form_id,
        page_id: page?.page_id,
        pipeline_id: data.pipeline.value,
        pipeline_stage_id: data.stage.value,
        lead_source_id: data.leadSource.value,
        priority: data.priority.value,
        followup_date: data.followupDate.value,
        round_robin_id: data.roundRobin.value,
        user_login_id: customer?.customer_details?.id,
        ...(markedLabelArray.length > 0 && { marked_label: markedLabelArray }),
        ...(customFieldArray.length > 0 && { custom_field: customFieldArray }),
      };
      const res = await PostRequest(
        `${baseURL}facebook/formSubmit`,
        payload,
        false,
        { headers },
        extras,
      );
      if (res?.status === "success") {
        onClose();
        fetchPages()
        toast.success(res?.message || "");
      } else {
        toast.error(res?.message || "Something went wrong!");
      }
    } catch (err) {
      console.error("Submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    (async () => {
      try {
        const res = await GetRequest(
          `/common/fetchCustomFields/${customerId}/leads`,
          { headers },
          extras
        );

        if (res.success === false || res.success === "fail") {
          toast.error(res.message);
          return;
        }
        // fields come from both `grouped` (inside groupFields) and `ungrouped`
        const groupedData = Array.isArray(res.data?.grouped)
          ? res.data.grouped
          : [];
        const ungroupedData = Array.isArray(res.data?.ungrouped)
          ? res.data.ungrouped
          : [];
        const allFields = [
          ...groupedData.flatMap((g) => g?.groupFields || []),
          ...ungroupedData,
        ];
        setDynamicFields(allFields);
      } catch (error) {
        toast.error("Internal Server Error");
        return;
      }
    })();
  }, [customer]);

  const handleClose = () => {
    setEditedData([]);
    setLabelMapping({});
    setLabelMappingErrors({});
    onClose();
  };
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 38,
      fontSize: 13,
      padding: "0 4px",
      boxShadow: "none",
      border: state.isFocused ? "1px solid #2684FF" : "1px solid #d1d5db",
      "&:hover": {
        borderColor: "#60a5fa",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 4px",
    }),
    input: (base) => ({
      ...base,
      padding: "2px 0",
      margin: 0,
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: 13,
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: 13,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 34,
      padding: "0 3px",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "4px",
    }),
    option: (base) => ({
      ...base,
      fontSize: 13,
      padding: "6px 10px",
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: 200,
      overflowY: "auto",
      scrollBehavior: "smooth",
      "::-webkit-scrollbar": {
        width: "8px",
      },
      "::-webkit-scrollbar-track": {
        background: "#f1f1f1",
        borderRadius: "4px",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#c9c9c9",
        borderRadius: "4px",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#888",
      },
    }),

    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: "#3b82f6",
      color: "white",
      fontSize: 12,
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: "white",
      fontWeight: 500,
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: "white",
      ":hover": {
        backgroundColor: "#ef4444",
        color: "white",
      },
    }),
  };
  return (
    <Offcanvas
      className="createContactOffcanvas d-flex flex-column"
      placement="end"
      backdrop="static"
      enforceFocus={true}
      show={show}
      onHide={handleClose}
    >
      <Offcanvas.Header closeButton>
          <div className="d-flex align-items-center gap-2">
              <div className="pt-0 ps-4">
        <Offcanvas.Title style={{ color: "#0791ff" }}
                  className="addEnquiryHead">
                    Add Lead
        </Offcanvas.Title>

            {page && (
              <p
                style={{
                    fontSize: "14px",
                    color: "#b26792",
                    lineHeight: 1.1,
                  }}
                  className="text-secondary text-nowrap mb-0"
              >
                {page.page_name} &mdash; {form?.form_name}
              </p>
            )}
          
        
        </div></div> 
      </Offcanvas.Header>

      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="form-container d-flex flex-column"
        style={{ flex: "1 1 auto", minHeight: 0 }}
      >
        <Offcanvas.Body
          className="flex-grow-1"
          style={{ overflowY: "auto", minHeight: 0 }}
        >
          {/* Read-only info */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small text-muted">
                  Form ID
                </Form.Label>
                <input
                  className="form-control form-control-sm bg-light"
                  value={form?.form_id || ""}
                  readOnly
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small text-muted">
                  Page ID
                </Form.Label>
                <input
                  className="form-control form-control-sm bg-light"
                  value={page?.page_id || ""}
                  readOnly
                />
              </Form.Group>
            </div>
          </div>

          <hr className="my-3" />

          {/* Pipeline + Stage */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small">
                  Pipeline <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="pipeline"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      options={pipelines}
                      placeholder="Select pipeline..."
                      isClearable
                      styles={customStyles}
                      classNamePrefix="react-select"
                    />
                  )}
                />
                {errors.pipeline && (
                  <div className="text-danger small mt-1">
                    {errors.pipeline.message}
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small">
                  Stage <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="stage"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      classNamePrefix="react-select"
                      {...field}
                      options={stages}
                      placeholder={
                        !selectedPipeline
                          ? "Select pipeline first..."
                          : stagesLoading
                            ? "Loading stages..."
                            : "Select stage..."
                      }
                      isLoading={stagesLoading}
                      isClearable
                    />
                  )}
                />
                {errors.stage && (
                  <div className="text-danger small mt-1">
                    {errors.stage.message}
                  </div>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Lead Source + Priority */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small">
                  Lead Source <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="leadSource"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      classNamePrefix="react-select"
                      {...field}
                      options={leadSources}
                      placeholder="Select lead source..."
                      isClearable
                    />
                  )}
                />
                {errors.leadSource && (
                  <div className="text-danger small mt-1">
                    {errors.leadSource.message}
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small">
                  Priority <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      classNamePrefix="react-select"
                      {...field}
                      options={PRIORITY_OPTIONS}
                      placeholder="Select priority..."
                      isClearable
                    />
                  )}
                />
                {errors.priority && (
                  <div className="text-danger small mt-1">
                    {errors.priority.message}
                  </div>
                )}
              </Form.Group>
            </div>
          </div>

          {/* Follow-up Date + Round Robin */}
          <div className="row g-2 mb-3">
            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small">
                  Follow-up Date <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="followupDate"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      classNamePrefix="react-select"
                      {...field}
                      options={getFollowupOptions()}
                      placeholder="Select follow-up date..."
                      isClearable
                    />
                  )}
                />
                {errors.followupDate && (
                  <div className="text-danger small mt-1">
                    {errors.followupDate.message}
                  </div>
                )}
              </Form.Group>
            </div>

            <div className="col-6">
              <Form.Group className="focus-z-index position-relative">
                <Form.Label className="form-label fw-semibold small">
                  Round Robin <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="roundRobin"
                  control={control}
                  render={({ field }) => (
                    <Select
                      styles={customStyles}
                      classNamePrefix="react-select"
                      {...field}
                      options={roundRobinData}
                      placeholder="Select Round Robin..."
                      isClearable
                    />
                  )}
                />
                {errors.roundRobin && (
                  <div className="text-danger small mt-1">
                    Round Robin is required
                  </div>
                )}
              </Form.Group>
            </div>
          </div>
          <div style={{ paddingBottom: "24px" }}>
            
            {mappingFields.length > 0 && (
              <>
                <hr className="my-3" />
                <Offcanvas.Title style={{ color: "#0791ff" }}
                  className="addEnquiryHead">
                    Form Mapping
                  </Offcanvas.Title>

                      {page && (
                        <p
                          style={{
                              fontSize: "14px",
                              color: "#b26792",
                              lineHeight: 1.1,
                            }}
                            className="text-secondary text-nowrap mb-0"
                        >
                          {page.page_name} &mdash; {form?.form_name}
                        </p>
                      )}

                <div className="border rounded bg-white shadow-sm mt-3">
                  <div
                    className="d-flex bg-light p-2 border-bottom text-muted fw-bold"
                    style={{ fontSize: "11px", letterSpacing: "0.05rem" }}
                  >
                    <div style={{ width: "45%", paddingLeft: "10px" }}>
                      SYSTEM FIELD
                    </div>
                    <div style={{ width: "55%", paddingLeft: "10px" }}>
                      FIELD TO MAP
                    </div>
                  </div>
                  {mappingFields.map((field, index) => {
                    const labelKey = field.key;

                    const isRequired = REQUIRED_LABEL_KEYS.includes(labelKey);
                    const hasError = !!labelMappingErrors[labelKey];

                    return (
                      <div
                        key={labelKey}
                        className={`d-flex align-items-start p-2 ${
                          index !== mappingFields.length - 1 ? "border-bottom" : ""
                        }`}
                        style={{ transition: "background-color 0.2s" }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#fcfcfc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <div
                          style={{
                            width: "45%",
                            paddingLeft: "10px",
                            paddingTop: "8px",
                          }}
                          className="fs-14 fw-500"
                        >
                          {field.label}
                          {isRequired && (
                            <span className="text-danger ms-1">*</span>
                          )}
                        </div>

                        <div style={{ width: "55%" }}>
                          <Select
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            options={getFilteredOptions(labelKey)}
                            value={labelMapping[labelKey] || null}
                            onChange={(selected) => {
                              setLabelMapping((prev) => ({
                                ...prev,
                                [labelKey]: selected,
                              }));
                              if (selected && labelMappingErrors[labelKey]) {
                                setLabelMappingErrors((prev) => {
                                  const updated = { ...prev };
                                  delete updated[labelKey];
                                  return updated;
                                });
                              }
                            }}
                            isClearable
                            menuPlacement="auto"
                            placeholder="Choose column..."
                          />
                          {hasError && (
                            <div className="text-danger small mt-1">
                              {labelMappingErrors[labelKey]}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Offcanvas.Body>

        {/* Footer (normal flex child, so the body above scrolls fully) */}
        <div
          className="d-flex justify-content-end gap-2 px-3 py-3"
          style={{
            flexShrink: 0,
            borderTop: "1px solid #e0eaff",
            backgroundColor: "white",
          }}
        >
          <button
            type="button"
            className="btn btn-default outline btn-md"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-default btn-md"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                />
                Saving...
              </>
            ) : (
              "Save Mapping"
            )}
          </button>
        </div>
      </Form>
    </Offcanvas>
  );
};

export default FacebookModal;